import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/lib/hooks/useToast'
import { tenantService, type CreateTenantData } from '@/lib/services/tenantService'
import { Loader2 } from 'lucide-react'

interface CreateTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

/**
 * Dialog for creating a new tenant whitelist entry
 */
export function CreateTenantDialog({ open, onOpenChange, onSuccess }: CreateTenantDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [formData, setFormData] = useState<CreateTenantData>({
    tenantId: '',
    tenantName: '',
    domain: '',
    notes: '',
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateTenantData) => tenantService.create(data),
    onSuccess: () => {
      toast({
        title: t('admin.tenants.createSuccess', 'Tenant Added'),
        description: t(
          'admin.tenants.createSuccessDescription',
          'Tenant has been added to the whitelist.'
        ),
        variant: 'success',
      })
      onSuccess()
      onOpenChange(false)
      resetForm()
    },
    onError: (error: Error) => {
      toast({
        title: t('admin.tenants.createError', 'Failed to Add Tenant'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const resetForm = () => {
    setFormData({
      tenantId: '',
      tenantName: '',
      domain: '',
      notes: '',
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await createMutation.mutateAsync(formData)
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('admin.tenants.createTenant', 'Add New Tenant')}</DialogTitle>
          <DialogDescription>
            {t(
              'admin.tenants.createTenantDescription',
              'Add a new Azure AD tenant to the whitelist. Users from this tenant will be able to sign in with Microsoft SSO.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tenant ID */}
          <div className="space-y-2">
            <Label htmlFor="tenantId">
              {t('admin.tenants.tenantId', 'Azure Tenant ID')} *
            </Label>
            <Input
              id="tenantId"
              name="tenantId"
              value={formData.tenantId}
              onChange={handleInputChange}
              placeholder="00000000-0000-0000-0000-000000000000"
              required
              pattern="[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
            />
            <p className="text-sm text-muted-foreground">
              {t(
                'admin.tenants.tenantIdHelp',
                'The Azure AD tenant ID (GUID format). Find this in Azure Portal > Azure Active Directory > Overview.'
              )}
            </p>
          </div>

          {/* Tenant Name */}
          <div className="space-y-2">
            <Label htmlFor="tenantName">
              {t('admin.tenants.tenantName', 'Tenant Name')} *
            </Label>
            <Input
              id="tenantName"
              name="tenantName"
              value={formData.tenantName}
              onChange={handleInputChange}
              placeholder={t('admin.tenants.tenantNamePlaceholder', 'Example Organization')}
              required
            />
            <p className="text-sm text-muted-foreground">
              {t('admin.tenants.tenantNameHelp', 'Display name for this tenant.')}
            </p>
          </div>

          {/* Domain */}
          <div className="space-y-2">
            <Label htmlFor="domain">
              {t('admin.tenants.domain', 'Primary Domain')} *
            </Label>
            <Input
              id="domain"
              name="domain"
              value={formData.domain}
              onChange={handleInputChange}
              placeholder="example.com"
              required
            />
            <p className="text-sm text-muted-foreground">
              {t(
                'admin.tenants.domainHelp',
                'The primary domain for this tenant (e.g., example.com).'
              )}
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('admin.tenants.notes', 'Notes')}</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={t(
                'admin.tenants.notesPlaceholder',
                'Optional notes about this tenant...'
              )}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              {t('admin.tenants.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('admin.tenants.addTenant', 'Add Tenant')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
